<template>
  <v-tooltip
    ref="vTooltipRef"
    :modelValue="internalOpen"
    :location="getLocation"
    :width="resolvedWidth"
    :min-width="resolvedMinWidth"
    :max-width="resolvedMaxWidth"
    :offset="resolvedOffset"
    :content-class="contentClass"
    :style="currentStyles"
    attach="body"
    :open-delay="resolvedOpenDelay"
    :close-delay="resolvedCloseDelay"
    :interactive="resolvedInteractive"
    :transition="resolvedTransition"
    :open-on-hover="resolvedOpenOnHover"
    :open-on-focus="resolvedOpenOnFocus"
    :disabled="disabled"
    :content-props="contentProps"
    @update:modelValue="handleModelUpdate"
  >
    <template #activator="{ props: activatorProps }">
      <slot
        name="activator"
        :props="getActivatorSlotProps(activatorProps)"
        :trigger-ref="setTriggerRef"
      >
        <span v-bind="getActivatorSlotProps(activatorProps)" ref="triggerRef">
          <slot></slot>
        </span>
      </slot>
    </template>
    <template v-if="$slots.content">
      <slot name="content"></slot>
    </template>
    <template v-else>
      <div
        class="wasabi-tooltip__content wasabi-tooltip__content--default"
        :class="resolvedTextClass"
        :data-testid="sectionTag ? `${sectionTag}-text` : undefined"
      >
        <span v-dompurify-html="description"></span>
      </div>
    </template>
  </v-tooltip>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onBeforeUnmount, onUpdated, useSlots } from 'vue'
import type { ComponentPublicInstance, CSSProperties } from 'vue'
import type { Anchor } from '@/types/Anchor'
import { labelTooltipDelayGroup } from './tooltipContext'

export type WTooltipIntent = 'label' | 'help' | 'definition' | 'overflow' | 'status'
export type WTooltipAriaMode = 'description' | 'visual-label'
export type WTooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'none'
export type WTooltipSize = 'hug' | 'compact' | 'standard' | 'wide'
export type WTooltipTransition = string | boolean
export type WTooltipDimension = string | number
export type WTooltipDelay = string | number
type WTooltipInteractionMode = 'hover' | 'focus'

export interface WTooltipProps {
  sectionTag?: string
  position?: WTooltipPosition
  description?: string
  width?: WTooltipDimension
  size?: WTooltipSize
  minWidth?: WTooltipDimension
  maxWidth?: WTooltipDimension
  modelValue?: boolean
  overrideHover?: boolean
  overflowOnly?: boolean
  intent?: WTooltipIntent
  ariaMode?: WTooltipAriaMode
  openDelay?: WTooltipDelay
  closeDelay?: WTooltipDelay
  hoverOpenDelay?: WTooltipDelay
  focusOpenDelay?: WTooltipDelay
  hoverCloseDelay?: WTooltipDelay
  focusCloseDelay?: WTooltipDelay
  interactive?: boolean
  transition?: WTooltipTransition
  showArrow?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<WTooltipProps>(), {
  sectionTag: 'unspecified',
  position: 'top',
  description: 'This is a tooltip.',
  width: undefined,
  size: undefined,
  minWidth: undefined,
  maxWidth: undefined,
  modelValue: undefined,
  overrideHover: false,
  overflowOnly: false,
  intent: undefined,
  ariaMode: undefined,
  openDelay: undefined,
  closeDelay: undefined,
  hoverOpenDelay: undefined,
  focusOpenDelay: undefined,
  hoverCloseDelay: undefined,
  focusCloseDelay: undefined,
  interactive: undefined,
  transition: undefined,
  showArrow: undefined,
  disabled: false
})

const slots = useSlots()

// With no text and no content slot the tooltip would open as an empty box, e.g. an overflowing
// custom table cell whose raw value is blank. A plain function, not a computed: a computed wrapping
// useSlots() does not re-track when a parent starts or stops supplying the slot, so this is read
// fresh on every open request instead.
const hasContent = (): boolean =>
  !!slots.content || (typeof props.description === 'string' && props.description.trim().length > 0)

type TooltipStyleVars = CSSProperties & Record<`--${string}`, string>

interface TooltipIntentDefault {
  hoverOpenDelay: number
  focusOpenDelay: number
  hoverCloseDelay: number
  focusCloseDelay: number
  transition: string | false
  interactive: boolean
  showArrow: boolean
  size: WTooltipSize
  maxWidth: number
  paddingX: string
  paddingY: string
  textClass: string
  contentGap: string
}

interface TooltipSizeDefault {
  inlineSize: string
  minWidth: number
  maxWidth: number
}

const INSET_EXTRA_TIGHT = 'var(--spacing-inset-container-extra-tight)'
const INSET_TIGHT = 'var(--spacing-inset-container-tight)'

const TOOLTIP_SIZE_DEFAULTS = {
  hug: {
    inlineSize: 'fit-content',
    minWidth: 0,
    maxWidth: 208,
  },
  compact: {
    inlineSize: 'fit-content',
    minWidth: 0,
    maxWidth: 220,
  },
  standard: {
    inlineSize: 'fit-content',
    minWidth: 0,
    maxWidth: 288,
  },
  wide: {
    inlineSize: 'fit-content',
    minWidth: 0,
    maxWidth: 320,
  },
} as const satisfies Record<WTooltipSize, TooltipSizeDefault>

const TOOLTIP_INTENT_DEFAULTS = {
  label: {
    hoverOpenDelay: 600,
    focusOpenDelay: 100,
    hoverCloseDelay: 100,
    focusCloseDelay: 100,
    transition: 'w-tooltip-fade-transition',
    interactive: true,
    showArrow: false,
    size: 'hug',
    maxWidth: 208,
    paddingX: INSET_EXTRA_TIGHT,
    paddingY: INSET_EXTRA_TIGHT,
    textClass: 'label-regular-small',
    contentGap: '0px',
  },
  help: {
    hoverOpenDelay: 450,
    focusOpenDelay: 100,
    hoverCloseDelay: 150,
    focusCloseDelay: 150,
    transition: 'w-tooltip-fade-shift-transition',
    interactive: true,
    showArrow: true,
    size: 'standard',
    maxWidth: 288,
    paddingX: INSET_TIGHT,
    paddingY: INSET_EXTRA_TIGHT,
    textClass: 'label-regular-small',
    contentGap: '0px',
  },
  definition: {
    hoverOpenDelay: 450,
    focusOpenDelay: 100,
    hoverCloseDelay: 150,
    focusCloseDelay: 150,
    transition: 'w-tooltip-fade-shift-transition',
    interactive: true,
    showArrow: true,
    size: 'compact',
    maxWidth: 252,
    paddingX: INSET_TIGHT,
    paddingY: INSET_EXTRA_TIGHT,
    textClass: 'label-regular-small',
    contentGap: '2px',
  },
  overflow: {
    hoverOpenDelay: 350,
    focusOpenDelay: 100,
    hoverCloseDelay: 100,
    focusCloseDelay: 100,
    transition: 'w-tooltip-fade-transition',
    interactive: true,
    showArrow: false,
    size: 'standard',
    maxWidth: 292,
    paddingX: INSET_TIGHT,
    paddingY: INSET_EXTRA_TIGHT,
    textClass: 'label-regular-small',
    contentGap: '0px',
  },
  status: {
    hoverOpenDelay: 0,
    focusOpenDelay: 0,
    hoverCloseDelay: 0,
    focusCloseDelay: 0,
    transition: 'w-tooltip-fade-transition',
    interactive: false,
    showArrow: false,
    size: 'hug',
    maxWidth: 208,
    paddingX: INSET_EXTRA_TIGHT,
    paddingY: INSET_EXTRA_TIGHT,
    textClass: 'label-regular-small',
    contentGap: '6px',
  },
} as const satisfies Record<WTooltipIntent, TooltipIntentDefault>

const vTooltipRef = ref<InstanceType<typeof import('vuetify/components').VTooltip> | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const effectiveDirection = ref<'top' | 'bottom' | 'start' | 'end'>('top')
const activeInteractionMode = ref<WTooltipInteractionMode>('hover')
const pendingOpenInteractionMode = ref<WTooltipInteractionMode | null>(null)
const openedInteractionMode = ref<WTooltipInteractionMode | null>(null)
const labelDelayGroupId = Symbol('WTooltipLabelDelayGroup')
const isHoverOpenedLabelTooltip = ref(false)
const suppressTransitionForGroupClose = ref(false)
const handoffHiddenContentEl = ref<HTMLElement | undefined>()
const isLabelActivatorHovered = ref(false)
const isLabelActivatorFocused = ref(false)
const isLabelContentHovered = ref(false)
let labelOpenTimer: ReturnType<typeof setTimeout> | undefined
let labelCloseTimer: ReturnType<typeof setTimeout> | undefined

const tooltipBg = 'var(--color-background-surface-inverse)'
const shadowColor = 'rgba(0, 0, 0, 0.16)'

const resolvedIntent = computed<WTooltipIntent>(() => props.intent ?? (props.overflowOnly ? 'overflow' : 'help'))
const isLabelIntent = computed(() => resolvedIntent.value === 'label')
const usesControlledLabelActivation = computed(() => isLabelIntent.value && !props.overrideHover)
const resolvedAriaMode = computed<WTooltipAriaMode>(() =>
  props.ariaMode ?? (resolvedIntent.value === 'label' ? 'visual-label' : 'description')
)
const intentDefaults = computed(() => TOOLTIP_INTENT_DEFAULTS[resolvedIntent.value])
const resolvedSize = computed<WTooltipSize>(() => props.size ?? intentDefaults.value.size)
const sizeDefaults = computed(() => TOOLTIP_SIZE_DEFAULTS[resolvedSize.value])
const hasExplicitWidth = computed(() => props.width !== undefined && String(props.width).trim() !== '')
const resolvedWidth = computed(() => hasExplicitWidth.value ? props.width : undefined)
const resolvedMinWidth = computed(() => props.minWidth ?? sizeDefaults.value.minWidth)
const resolvedMaxWidth = computed(() => {
  if (props.maxWidth !== undefined) return props.maxWidth
  if (hasExplicitWidth.value && !isIntrinsicWidth(props.width)) return props.width
  if (props.size !== undefined) return sizeDefaults.value.maxWidth
  return intentDefaults.value.maxWidth
})
const resolvedBaseHoverOpenDelay = computed(() => props.hoverOpenDelay ?? props.openDelay ?? intentDefaults.value.hoverOpenDelay)
const hasExplicitHoverOpenDelay = computed(() => props.hoverOpenDelay !== undefined || props.openDelay !== undefined)
const usesLabelSkipOpenDelay = computed(() =>
  resolvedIntent.value === 'label' &&
  !hasExplicitHoverOpenDelay.value &&
  labelTooltipDelayGroup.isInstantPhase.value
)
const resolvedHoverOpenDelay = computed(() => {
  if (usesLabelSkipOpenDelay.value) {
    return labelTooltipDelayGroup.skipOpenDelay
  }

  return resolvedBaseHoverOpenDelay.value
})
const resolvedFocusOpenDelay = computed(() => props.focusOpenDelay ?? props.openDelay ?? intentDefaults.value.focusOpenDelay)
const resolvedHoverCloseDelay = computed(() => props.hoverCloseDelay ?? props.closeDelay ?? intentDefaults.value.hoverCloseDelay)
const resolvedFocusCloseDelay = computed(() => props.focusCloseDelay ?? props.closeDelay ?? intentDefaults.value.focusCloseDelay)
const resolvedOpenDelay = computed(() =>
  activeInteractionMode.value === 'focus' ? resolvedFocusOpenDelay.value : resolvedHoverOpenDelay.value
)
const resolvedCloseDelay = computed(() =>
  activeInteractionMode.value === 'focus' ? resolvedFocusCloseDelay.value : resolvedHoverCloseDelay.value
)
const resolvedInteractive = computed(() => props.interactive ?? intentDefaults.value.interactive)
const resolvedTransition = computed(() => {
  if (suppressTransitionForGroupClose.value) return false

  return props.transition ?? intentDefaults.value.transition
})
const resolvedShowArrow = computed(() => props.position !== 'none' && (props.showArrow ?? intentDefaults.value.showArrow))
const resolvedTextClass = computed(() => intentDefaults.value.textClass)
const resolvedOffset = computed(() => resolvedShowArrow.value ? 10 : 8)
const resolvedOpenOnHover = computed(() =>
  !props.overrideHover && !isLabelIntent.value && resolvedIntent.value !== 'status'
)
const resolvedOpenOnFocus = computed(() =>
  !props.overrideHover && !isLabelIntent.value && resolvedIntent.value !== 'status'
)
const contentClass = computed(() => [
  'wasabi-tooltip',
  `wasabi-tooltip--${resolvedIntent.value}`,
  `wasabi-tooltip--size-${resolvedSize.value}`,
  `wasabi-tooltip--side-${effectiveDirection.value}`,
  resolvedShowArrow.value ? 'wasabi-tooltip--with-arrow' : 'wasabi-tooltip--without-arrow',
].join(' '))

const toCssLength = (value: WTooltipDimension | undefined, fallback: string): string => {
  if (value === undefined) return fallback
  if (typeof value === 'number') return `${value}px`

  const trimmedValue = value.trim()
  if (!trimmedValue) return fallback

  return /^-?\d+(\.\d+)?$/.test(trimmedValue) ? `${trimmedValue}px` : trimmedValue
}

const isIntrinsicWidth = (value: WTooltipDimension | undefined): boolean => {
  if (value === undefined || typeof value === 'number') return false

  return ['auto', 'fit-content', 'max-content', 'min-content'].includes(value.trim())
}

const tooltipStyles = computed<TooltipStyleVars>(() => ({
  '--w-tooltip-inline-size': hasExplicitWidth.value ? toCssLength(props.width, 'auto') : sizeDefaults.value.inlineSize,
  '--w-tooltip-min-inline-size': toCssLength(resolvedMinWidth.value, '0px'),
  '--w-tooltip-max-inline-size': toCssLength(resolvedMaxWidth.value, '9999px'),
  '--w-tooltip-padding-x': intentDefaults.value.paddingX,
  '--w-tooltip-padding-y': intentDefaults.value.paddingY,
  '--w-tooltip-content-gap': intentDefaults.value.contentGap,
}))

const getLocation = computed(() => {
  if (props.position === 'none') return 'top' as Anchor
  const currentLocation = props.position
    .toLowerCase()
    .replace(/[-_]/g, ' ')
    .replace('left', 'start')
    .replace('right', 'end')
  return currentLocation as Anchor
})

const emit = defineEmits(['update:modelValue'])
const localOpen = ref(false)
const overflowVisible = ref(false)
const overflowDismissed = ref(false)
type TooltipActivatorElement = Element | ComponentPublicInstance | null
interface TemplateRef {
  value: TooltipActivatorElement
}

const FOCUSABLE_CONTROL_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
].join(',')

let hasWarnedMissingLabelActivatorName = false

const measureOverflow = (): boolean => {
  if (!triggerRef.value) return false
  const target = triggerRef.value.firstElementChild ?? triggerRef.value
  return target.scrollWidth > target.clientWidth
}

const checkOverflow = () => {
  if (!props.overflowOnly || !triggerRef.value) return
  overflowDismissed.value = false
  overflowVisible.value = measureOverflow()
}

// The enter/focus measurement can land while the trigger is still resizing (a sibling animating
// into the same flex row, for example). Only while the activator is active, follow the trigger's
// size so text that becomes hidden a moment later still reveals itself. The observer exists only
// for the active trigger, so idle tooltips cost nothing.
const isOverflowActivatorHovered = ref(false)
const isOverflowActivatorFocused = ref(false)
const isOverflowActivatorActive = computed(() => isOverflowActivatorHovered.value || isOverflowActivatorFocused.value)
// Set when Vuetify asks to open after its own delay; a later resize into overflow honours that
// request instead of opening early.
const overflowOpenRequested = ref(false)
let overflowResizeObserver: ResizeObserver | undefined

const handleOverflowTriggerResize = (): void => {
  if (!props.overflowOnly || !isOverflowActivatorActive.value || overflowDismissed.value) return

  const nowVisible = measureOverflow()
  if (nowVisible === overflowVisible.value) return

  overflowVisible.value = nowVisible
  localOpen.value = nowVisible && overflowOpenRequested.value
}

const activateOverflowTrigger = (mode: 'hover' | 'focus'): void => {
  if (mode === 'hover') isOverflowActivatorHovered.value = true
  else isOverflowActivatorFocused.value = true
  if (!props.overflowOnly || !triggerRef.value || typeof ResizeObserver === 'undefined') return

  overflowResizeObserver ??= new ResizeObserver(handleOverflowTriggerResize)
  overflowResizeObserver.observe(triggerRef.value)
}

// Hover and focus are tracked apart so the mouse leaving a still-focused activator does not stop
// following its size.
const deactivateOverflowTrigger = (mode: 'hover' | 'focus'): void => {
  if (mode === 'hover') isOverflowActivatorHovered.value = false
  else isOverflowActivatorFocused.value = false
  if (isOverflowActivatorActive.value) return

  overflowOpenRequested.value = false
  overflowResizeObserver?.disconnect()
}

const clearOverflow = () => {
  if (!props.overflowOnly) return
  overflowVisible.value = false
  overflowDismissed.value = false
  localOpen.value = false
}

const clearLabelOpenTimer = (): void => {
  if (labelOpenTimer === undefined) return

  clearTimeout(labelOpenTimer)
  labelOpenTimer = undefined
}

const clearLabelCloseTimer = (): void => {
  if (labelCloseTimer === undefined) return

  clearTimeout(labelCloseTimer)
  labelCloseTimer = undefined
}

const clearLabelTimers = (): void => {
  clearLabelOpenTimer()
  clearLabelCloseTimer()
}

const clearHandoffHiddenContent = (): void => {
  handoffHiddenContentEl.value?.classList.remove('wasabi-tooltip--handoff-hidden')
  handoffHiddenContentEl.value = undefined
}

const hideCurrentTooltipContentForHandoff = (): void => {
  const contentEl = vTooltipRef.value?.contentEl
  if (!contentEl) return

  clearHandoffHiddenContent()
  contentEl.classList.add('wasabi-tooltip--handoff-hidden')
  handoffHiddenContentEl.value = contentEl
}

const toDelayMilliseconds = (value: WTooltipDelay): number => {
  const delay = Number(value)
  return Number.isFinite(delay) ? Math.max(0, delay) : 0
}

const hasActiveControlledLabelAffinity = (): boolean => {
  return isLabelActivatorHovered.value || isLabelActivatorFocused.value || isLabelContentHovered.value
}

const shouldKeepControlledLabelOpen = (): boolean => {
  return usesControlledLabelActivation.value && !props.disabled && hasActiveControlledLabelAffinity()
}

const setTooltipOpen = (value: boolean): void => {
  if (props.modelValue !== undefined) {
    emit('update:modelValue', value)
  } else {
    localOpen.value = value
  }
}

const openControlledLabelTooltip = (interactionMode: WTooltipInteractionMode): void => {
  if (!usesControlledLabelActivation.value) return
  if (props.disabled) return

  clearLabelTimers()
  activeInteractionMode.value = interactionMode
  pendingOpenInteractionMode.value = interactionMode

  labelTooltipDelayGroup.closeCurrentBeforeOpen(labelDelayGroupId)
  setTooltipOpen(true)
}

const closeControlledLabelTooltip = (force = false): void => {
  if (!usesControlledLabelActivation.value) return
  if (!force && shouldKeepControlledLabelOpen()) return

  clearLabelTimers()
  pendingOpenInteractionMode.value = null
  setTooltipOpen(false)
}

const scheduleControlledLabelOpen = (interactionMode: WTooltipInteractionMode): void => {
  if (props.disabled) return

  clearLabelOpenTimer()
  clearLabelCloseTimer()
  activeInteractionMode.value = interactionMode
  pendingOpenInteractionMode.value = interactionMode

  if (interactionMode === 'hover' && usesLabelSkipOpenDelay.value) {
    labelTooltipDelayGroup.closeCurrentBeforeOpen(labelDelayGroupId)
  }

  const delay = interactionMode === 'focus' ? resolvedFocusOpenDelay.value : resolvedHoverOpenDelay.value
  labelOpenTimer = setTimeout(() => {
    labelOpenTimer = undefined
    openControlledLabelTooltip(interactionMode)
  }, toDelayMilliseconds(delay))
}

const scheduleControlledLabelClose = (interactionMode: WTooltipInteractionMode): void => {
  if (shouldKeepControlledLabelOpen()) {
    clearLabelCloseTimer()
    return
  }

  clearLabelOpenTimer()
  clearLabelCloseTimer()
  activeInteractionMode.value = interactionMode
  pendingOpenInteractionMode.value = null

  const delay = interactionMode === 'focus' ? resolvedFocusCloseDelay.value : resolvedHoverCloseDelay.value
  labelCloseTimer = setTimeout(() => {
    labelCloseTimer = undefined
    closeControlledLabelTooltip()
  }, toDelayMilliseconds(delay))
}

const handleLabelContentMouseenter = (): void => {
  if (!usesControlledLabelActivation.value) return

  isLabelContentHovered.value = true
  clearLabelCloseTimer()
}

const handleLabelContentMouseleave = (): void => {
  if (!usesControlledLabelActivation.value) return

  isLabelContentHovered.value = false
  scheduleControlledLabelClose('hover')
}

const contentProps = computed(() => {
  if (!usesControlledLabelActivation.value) return undefined

  return {
    onMouseenter: handleLabelContentMouseenter,
    onMouseleave: handleLabelContentMouseleave,
  }
})

const callActivatorListener = (listener: unknown, event: Event): void => {
  if (typeof listener === 'function') {
    listener(event)
    return
  }

  if (Array.isArray(listener)) {
    listener.forEach(handler => callActivatorListener(handler, event))
  }
}

const queueActivatorListener = (
  listener: unknown,
  event: Event,
  interactionMode: WTooltipInteractionMode,
  phase: 'open' | 'close',
): void => {
  activeInteractionMode.value = interactionMode
  pendingOpenInteractionMode.value = phase === 'open' ? interactionMode : null

  nextTick(() => {
    callActivatorListener(listener, event)
  })
}

const getLabelActivatorControl = (): HTMLElement | null => {
  if (!triggerRef.value) return null
  if (triggerRef.value.matches(FOCUSABLE_CONTROL_SELECTOR)) return triggerRef.value

  return triggerRef.value.querySelector<HTMLElement>(FOCUSABLE_CONTROL_SELECTOR)
}

const hasAttributeValue = (element: HTMLElement, attribute: string): boolean => {
  return Boolean(element.getAttribute(attribute)?.trim())
}

const hasAccessibleName = (element: HTMLElement): boolean => {
  if (hasAttributeValue(element, 'aria-label')) return true
  if (hasAttributeValue(element, 'aria-labelledby')) return true
  if (element.textContent?.trim()) return true

  return Array
    .from(element.querySelectorAll<HTMLImageElement>('img[alt]'))
    .some(image => Boolean(image.getAttribute('alt')?.trim()))
}

const warnMissingLabelActivatorName = (): void => {
  if (!import.meta.env.DEV) return
  if (resolvedIntent.value !== 'label') return
  if (hasWarnedMissingLabelActivatorName) return

  const activatorControl = getLabelActivatorControl()
  if (!activatorControl || hasAccessibleName(activatorControl)) return

  hasWarnedMissingLabelActivatorName = true
  console.warn(
    '[WTooltip] intent="label" is a visual label only. Add an accessible name to the activator before using a label tooltip, such as aria-label or aria-labelledby.',
    activatorControl,
  )
}

const setTriggerRef = (element: TooltipActivatorElement): void => {
  if (!element) {
    triggerRef.value = null
    return
  }

  if (element instanceof HTMLElement) {
    triggerRef.value = element
    nextTick(warnMissingLabelActivatorName)
    return
  }

  if ('$el' in element && element.$el instanceof HTMLElement) {
    triggerRef.value = element.$el
    nextTick(warnMissingLabelActivatorName)
    return
  }

  triggerRef.value = null
}

const isTemplateRef = (activatorRef: unknown): activatorRef is TemplateRef => {
  return typeof activatorRef === 'object' && activatorRef !== null && 'value' in activatorRef
}

const setActivatorRef = (activatorRef: unknown, element: TooltipActivatorElement): void => {
  if (typeof activatorRef === 'function') {
    activatorRef(element)
    return
  }

  if (Array.isArray(activatorRef)) {
    activatorRef.forEach(refItem => setActivatorRef(refItem, element))
    return
  }

  if (isTemplateRef(activatorRef)) {
    activatorRef.value = element
  }
}

const composeActivatorRef = (activatorRef: unknown) => (element: TooltipActivatorElement): void => {
  setTriggerRef(element)
  setActivatorRef(activatorRef, element)
}

const getSemanticActivatorProps = (activatorProps: Record<string, unknown>): Record<string, unknown> => {
  const semanticActivatorProps = { ...activatorProps }

  if (resolvedAriaMode.value === 'visual-label') {
    delete semanticActivatorProps['aria-describedby']
  }

  return semanticActivatorProps
}

const getActivatorSlotProps = (activatorProps: Record<string, unknown>) => {
  const semanticActivatorProps = getSemanticActivatorProps(activatorProps)

  return {
    ...semanticActivatorProps,
    ref: composeActivatorRef(activatorProps.ref),
    onMouseenter: (event: MouseEvent) => {
      if (usesControlledLabelActivation.value) {
        isLabelActivatorHovered.value = true
        scheduleControlledLabelOpen('hover')
        return
      }

      queueActivatorListener(activatorProps.onMouseenter, event, 'hover', 'open')
      activateOverflowTrigger('hover')
      checkOverflow()
    },
    onMouseleave: (event: MouseEvent) => {
      if (usesControlledLabelActivation.value) {
        isLabelActivatorHovered.value = false
        scheduleControlledLabelClose('hover')
        return
      }

      queueActivatorListener(activatorProps.onMouseleave, event, 'hover', 'close')
      deactivateOverflowTrigger('hover')
      clearOverflow()
    },
    onFocus: (event: FocusEvent) => {
      if (usesControlledLabelActivation.value) {
        isLabelActivatorFocused.value = true
        scheduleControlledLabelOpen('focus')
        return
      }

      queueActivatorListener(activatorProps.onFocus, event, 'focus', 'open')
      activateOverflowTrigger('focus')
      checkOverflow()
    },
    onBlur: (event: FocusEvent) => {
      if (usesControlledLabelActivation.value) {
        isLabelActivatorFocused.value = false
        scheduleControlledLabelClose('focus')
        return
      }

      queueActivatorListener(activatorProps.onBlur, event, 'focus', 'close')
      deactivateOverflowTrigger('focus')
      clearOverflow()
    },
    onKeydown: (event: KeyboardEvent) => {
      if (event.key === 'Escape' && internalOpen.value) {
        event.stopPropagation()
        handleModelUpdate(false)
        return
      }

      callActivatorListener(activatorProps.onKeydown, event)
    },
  }
}

// The content check runs last so the open state is always read and tracked; an early return on
// missing content would leave this computed with no dependency on the open state, stuck closed.
const internalOpen = computed(() => {
  if (props.disabled) return false
  const requestedOpen = props.overflowOnly
    ? overflowVisible.value && !overflowDismissed.value && localOpen.value
    : props.modelValue !== undefined
      ? props.modelValue
      : localOpen.value
  return requestedOpen && hasContent()
})

const getRequestedDirection = (): typeof effectiveDirection.value => {
  return getLocation.value.split(' ')[0] as typeof effectiveDirection.value
}

// Detect if Vuetify flipped the tooltip placement and update arrow direction accordingly.
// Adapted from WFlyout.vue recalculateArrowDirection() — uses --v-overlay-anchor-origin CSS variable.
const recalculateArrowDirection = (): void => {
  const contentEl = vTooltipRef.value?.contentEl
  if (!contentEl) return

  const anchorOrigin = getComputedStyle(contentEl).getPropertyValue('--v-overlay-anchor-origin')
  if (!anchorOrigin) return

  const requested = getRequestedDirection()

  // Vuetify's anchor-origin tells us which side of the activator the overlay is anchored to.
  // Normal: anchor side MATCHES the requested direction (e.g., top→top).
  // Flipped: anchor side OPPOSES it (e.g., requested top but anchor says bottom → tooltip flipped below).
  if (requested === 'top' && anchorOrigin.includes('bottom')) {
    effectiveDirection.value = 'bottom'
  } else if (requested === 'bottom' && anchorOrigin.includes('top')) {
    effectiveDirection.value = 'top'
  } else if (requested === 'start' && anchorOrigin.includes('right')) {
    effectiveDirection.value = 'end'
  } else if (requested === 'end' && anchorOrigin.includes('left')) {
    effectiveDirection.value = 'start'
  } else {
    effectiveDirection.value = requested
  }
}

watch(() => props.position, () => {
  effectiveDirection.value = getRequestedDirection()
}, { immediate: true })

watch(resolvedIntent, () => {
  clearLabelTimers()
  isLabelActivatorHovered.value = false
  isLabelActivatorFocused.value = false
  isLabelContentHovered.value = false
  hasWarnedMissingLabelActivatorName = false
  nextTick(warnMissingLabelActivatorName)
})

watch(() => props.disabled, (disabled) => {
  if (disabled) {
    closeControlledLabelTooltip(true)
    return
  }

  if (!usesControlledLabelActivation.value || !hasActiveControlledLabelAffinity()) return

  scheduleControlledLabelOpen(isLabelActivatorFocused.value ? 'focus' : 'hover')
})

watch(internalOpen, (newValue, oldValue) => {
  if (newValue) {
    clearHandoffHiddenContent()
    openedInteractionMode.value = pendingOpenInteractionMode.value
    pendingOpenInteractionMode.value = null
    isHoverOpenedLabelTooltip.value = resolvedIntent.value === 'label' && openedInteractionMode.value === 'hover'

    if (isHoverOpenedLabelTooltip.value) {
      labelTooltipDelayGroup.markOpened(labelDelayGroupId)
    }

    nextTick(() => {
      updateTooltipStyles()
    })
    return
  }

  if (oldValue && isHoverOpenedLabelTooltip.value) {
    labelTooltipDelayGroup.markClosed(labelDelayGroupId)
  }

  openedInteractionMode.value = null
  isHoverOpenedLabelTooltip.value = false

  if (suppressTransitionForGroupClose.value) {
    nextTick(() => {
      suppressTransitionForGroupClose.value = false
    })
  }
})

onBeforeUnmount(() => {
  clearLabelTimers()
  clearHandoffHiddenContent()
  overflowResizeObserver?.disconnect()

  if (isHoverOpenedLabelTooltip.value) {
    labelTooltipDelayGroup.markClosed(labelDelayGroupId)
  }

  labelTooltipDelayGroup.unregister(labelDelayGroupId)
})

// Re-check arrow direction after Vuetify finishes positioning (may flip on subsequent renders).
let updatingStyles = false
onUpdated(() => {
  if (internalOpen.value && !updatingStyles) {
    updatingStyles = true
    nextTick(() => {
      updateTooltipStyles()
      nextTick(() => { updatingStyles = false })
    })
  }
})

const getEmptyArrowStyles = (): TooltipStyleVars => ({
  '--arrow-border-width': '0',
  '--arrow-border-color': 'transparent',
  '--arrow-top': 'auto',
  '--arrow-bottom': 'auto',
  '--arrow-left': 'auto',
  '--arrow-right': 'auto',
  '--arrow-transform': 'none',
  '--shadow-border-width': '0',
  '--shadow-border-color': 'transparent',
  '--shadow-top': 'auto',
  '--shadow-bottom': 'auto',
  '--shadow-left': 'auto',
  '--shadow-right': 'auto'
})

const arrowStyles = ref<TooltipStyleVars>(getEmptyArrowStyles())
const currentStyles = computed<TooltipStyleVars>(() => ({
  ...tooltipStyles.value,
  ...arrowStyles.value,
}))

const updateTooltipStyles = () => {
  recalculateArrowDirection()

  const arrowDirection = getArrowDirection.value
  const arrowPos = getArrowPosition.value

  arrowStyles.value = {
    '--arrow-border-width': arrowDirection.arrow.borderWidth,
    '--arrow-border-color': arrowDirection.arrow.borderColor,
    '--arrow-top': arrowPos.arrow.top,
    '--arrow-bottom': arrowPos.arrow.bottom,
    '--arrow-left': arrowPos.arrow.left,
    '--arrow-right': arrowPos.arrow.right,
    '--arrow-transform': arrowPos.arrow.transform,
    '--shadow-border-width': arrowDirection.shadow.borderWidth,
    '--shadow-border-color': arrowDirection.shadow.borderColor,
    '--shadow-top': arrowPos.shadow.top,
    '--shadow-bottom': arrowPos.shadow.bottom,
    '--shadow-left': arrowPos.shadow.left,
    '--shadow-right': arrowPos.shadow.right
  }
}

const handleModelUpdate = (value: boolean) => {
  if (props.overflowOnly) {
    overflowOpenRequested.value = value
    localOpen.value = value && overflowVisible.value && !overflowDismissed.value
    if (!value && overflowVisible.value) {
      overflowDismissed.value = true
    }
    return
  }

  setTooltipOpen(value)
}

const closeLabelTooltipImmediately = (): void => {
  if (resolvedIntent.value !== 'label' || !internalOpen.value) return

  clearLabelTimers()
  hideCurrentTooltipContentForHandoff()
  suppressTransitionForGroupClose.value = true
  pendingOpenInteractionMode.value = null
  closeControlledLabelTooltip(true)
}

labelTooltipDelayGroup.register(labelDelayGroupId, closeLabelTooltipImmediately)

const getArrowDirection = computed(() => {
  if (!resolvedShowArrow.value) {
    return {
      arrow: { borderWidth: '0', borderColor: 'transparent' },
      shadow: { borderWidth: '0', borderColor: 'transparent' }
    }
  }

  let arrowDirection = {
    arrow: {
      borderWidth: '',
      borderColor: ''
    },
    shadow: {
      borderWidth: '',
      borderColor: ''
    }
  }

  const primaryLocation = effectiveDirection.value

  if (primaryLocation === 'start') {
    arrowDirection = {
      arrow: {
        borderWidth: '7px 0 7px 8px',
        borderColor: `transparent transparent transparent ${tooltipBg}`
      },
      shadow: {
        borderWidth: '8px 0 8px 9px',
        borderColor: `transparent transparent transparent ${shadowColor}`
      }
    }
  } else if (primaryLocation === 'end') {
    arrowDirection = {
      arrow: {
        borderWidth: '7px 8px 7px 0',
        borderColor: `transparent ${tooltipBg} transparent transparent`
      },
      shadow: {
        borderWidth: '8px 9px 8px 0',
        borderColor: `transparent ${shadowColor} transparent transparent`
      }
    }
  } else if (primaryLocation === 'bottom') {
    arrowDirection = {
      arrow: {
        borderWidth: '0 7px 8px 7px',
        borderColor: `transparent transparent ${tooltipBg} transparent`
      },
      shadow: {
        borderWidth: '0 8px 9px 8px',
        borderColor: `transparent transparent ${shadowColor} transparent`
      }
    }
  } else if (primaryLocation === 'top') {
    arrowDirection = {
      arrow: {
        borderWidth: '8px 7px 0 7px',
        borderColor: `${tooltipBg} transparent transparent transparent`
      },
      shadow: {
        borderWidth: '9px 8px 0 8px',
        borderColor: `${shadowColor} transparent transparent transparent`
      }
    }
  }
  return arrowDirection
})

const getArrowPosition = computed(() => {
  if (!resolvedShowArrow.value) {
    return {
      arrow: { top: 'auto', bottom: 'auto', left: 'auto', right: 'auto', transform: 'none' },
      shadow: { top: 'auto', bottom: 'auto', left: 'auto', right: 'auto' }
    }
  }

  const arrowPositions = {
    arrow: {
      top: 'auto',
      bottom: 'auto',
      left: 'auto',
      right: 'auto',
      transform: 'none'
    },
    shadow: {
      top: 'auto',
      bottom: 'auto',
      left: 'auto',
      right: 'auto'
    }
  }
  const primaryLocation = effectiveDirection.value
  const secondaryLocation = getLocation.value.split(' ')[1]

  if (primaryLocation === 'start' || primaryLocation === 'end') {
    arrowPositions.arrow.top = '50%'
    arrowPositions.shadow.top = '50%'
    arrowPositions.arrow.transform = 'translateY(-50%)'
  }

  if (primaryLocation === 'start') {
    arrowPositions.arrow.right = '-8px'
    arrowPositions.shadow.right = '-9px'
  }

  if (primaryLocation === 'end') {
    arrowPositions.arrow.left = '-8px'
    arrowPositions.shadow.left = '-9px'
  }

  if (primaryLocation === 'top' || primaryLocation === 'bottom') {
    arrowPositions.arrow.left = '50%'
    arrowPositions.shadow.left = '50%'
    arrowPositions.arrow.transform = 'translateX(-50%)'
  }

  if (primaryLocation === 'top') {
    arrowPositions.arrow.bottom = '-8px'
    arrowPositions.shadow.bottom = '-9px'
  }

  if (primaryLocation === 'bottom') {
    arrowPositions.arrow.top = '-8px'
    arrowPositions.shadow.top = '-9px'
  }

  if (secondaryLocation) {
    if (secondaryLocation === 'start') {
      arrowPositions.arrow.left = '16px'
      arrowPositions.shadow.left = '16px'
      arrowPositions.arrow.right = 'auto'
      arrowPositions.shadow.right = 'auto'
    }

    if (secondaryLocation === 'end') {
      arrowPositions.arrow.right = '16px'
      arrowPositions.shadow.right = '16px'
      arrowPositions.arrow.left = 'auto'
      arrowPositions.shadow.left = 'auto'
    }
    arrowPositions.arrow.transform = 'none'
  }

  return arrowPositions
})
</script>

<style lang="scss">
.v-overlay__content.wasabi-tooltip {
  --w-tooltip-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.16);
  --w-tooltip-viewport-max-inline-size: calc(100vw - 48px);

  box-sizing: border-box;
  inline-size: var(--w-tooltip-inline-size);
  min-inline-size: min(var(--w-tooltip-min-inline-size), var(--w-tooltip-viewport-max-inline-size));
  max-inline-size: min(var(--w-tooltip-max-inline-size), var(--w-tooltip-viewport-max-inline-size));
  background-color: var(--color-background-surface-inverse) !important;
  padding: var(--w-tooltip-padding-y) var(--w-tooltip-padding-x) !important;
  box-shadow: var(--w-tooltip-shadow) !important;
  color: var(--color-content-text-inverse) !important;
  border-radius: var(--border-radius-layer-small) !important;
  overflow-wrap: break-word;
}

.v-overlay__content.wasabi-tooltip .wasabi-tooltip__content--default {
  display: flex;
  flex-direction: column;
  gap: var(--w-tooltip-content-gap);
  inline-size: auto;
  max-inline-size: 100%;
  min-inline-size: 0;
}

.v-overlay__content.wasabi-tooltip--handoff-hidden {
  opacity: 0 !important;
  pointer-events: none !important;
  transition: none !important;
  visibility: hidden !important;
}

.v-overlay__content.wasabi-tooltip::after,
.v-overlay__content.wasabi-tooltip::before {
  content: '';
  position: absolute;
  border-style: solid;
}

.v-overlay__content.wasabi-tooltip::before {
  filter: blur(1px);
  opacity: 0.28;
  border-width: var(--shadow-border-width);
  border-color: var(--shadow-border-color);
  top: var(--shadow-top);
  bottom: var(--shadow-bottom);
  left: var(--shadow-left);
  right: var(--shadow-right);
  transform: var(--arrow-transform);
}

.v-overlay__content.wasabi-tooltip::after {
  border-width: var(--arrow-border-width);
  border-color: var(--arrow-border-color);
  top: var(--arrow-top);
  bottom: var(--arrow-bottom);
  left: var(--arrow-left);
  right: var(--arrow-right);
  transform: var(--arrow-transform);
}

.w-tooltip-fade-transition-enter-active,
.w-tooltip-fade-transition-leave-active {
  transition-property: opacity;
}

.w-tooltip-fade-shift-transition-enter-active,
.w-tooltip-fade-shift-transition-leave-active {
  transition-property: opacity, transform;
}

.w-tooltip-fade-transition-enter-active {
  transition-duration: 80ms;
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
}

.w-tooltip-fade-transition-leave-active {
  transition-duration: 60ms;
  transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
}

.w-tooltip-fade-shift-transition-enter-active {
  transition-duration: 100ms;
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
}

.w-tooltip-fade-shift-transition-leave-active {
  transition-duration: 70ms;
  transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
}

.w-tooltip-fade-transition-enter-from,
.w-tooltip-fade-transition-leave-to,
.w-tooltip-fade-shift-transition-enter-from,
.w-tooltip-fade-shift-transition-leave-to {
  opacity: 0;
}

.w-tooltip-fade-shift-transition-enter-from.wasabi-tooltip--side-top,
.w-tooltip-fade-shift-transition-leave-to.wasabi-tooltip--side-top {
  transform: translateY(2px);
}

.w-tooltip-fade-shift-transition-enter-from.wasabi-tooltip--side-bottom,
.w-tooltip-fade-shift-transition-leave-to.wasabi-tooltip--side-bottom {
  transform: translateY(-2px);
}

.w-tooltip-fade-shift-transition-enter-from.wasabi-tooltip--side-start,
.w-tooltip-fade-shift-transition-leave-to.wasabi-tooltip--side-start {
  transform: translateX(2px);
}

.w-tooltip-fade-shift-transition-enter-from.wasabi-tooltip--side-end,
.w-tooltip-fade-shift-transition-leave-to.wasabi-tooltip--side-end {
  transform: translateX(-2px);
}

@media (prefers-reduced-motion: reduce) {
  .w-tooltip-fade-transition-enter-active,
  .w-tooltip-fade-transition-leave-active,
  .w-tooltip-fade-shift-transition-enter-active,
  .w-tooltip-fade-shift-transition-leave-active {
    transition-duration: 0ms !important;
    transition-delay: 0ms !important;
  }

  .w-tooltip-fade-shift-transition-enter-from,
  .w-tooltip-fade-shift-transition-enter-to,
  .w-tooltip-fade-shift-transition-leave-from,
  .w-tooltip-fade-shift-transition-leave-to {
    transform: none !important;
  }
}

/* Teleported tooltip content sits outside the component tree. Redeclare typography
   classes here so the tag() mixin resolves via :root CSS custom properties. */
.v-overlay__content.wasabi-tooltip {
  h1, h2 { @include tag(heading-bold-large); }
  h3 { @include tag(heading-bold-medium); }
  h4 { @include tag(heading-bold-small); }

  @each $name, $_ in $typography {
    .#{$name} { @include tag($name); }
  }

  @each $name, $_ in $typography-legacy {
    .#{$name} { @include tag($name); }
  }
}
</style>
